import React, { useState } from 'react';
import {
  Box,
  Grid,
  Container,
  Typography,
  useTheme,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Inventory2,
  Warehouse,
  Category,
  Warning,
  Refresh,
  TrendingUp
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { InventoryBreakdownChart } from 'src/components/InventoryBreakdownChart';
import { RecentActivityTimeline } from 'src/components/Recentactivitytimeline';
import { StaleInventoryTable } from 'src/components/StaleInventoryTable';
import { StatCard } from 'src/components/StatCard';
import { TopStorageUnits } from 'src/components/TopStorageUnits';
import { WarehouseUtilizationChart } from 'src/components/WarehouseUtilizationChart';
import { AlertsOverview } from 'src/components/AlertsOverview';
import { ActiveTransferOrders } from 'src/components/ActiveTransferOrders';
import { ShipmentsOverview } from 'src/components/ShipmentsOverview';
import { ReceivingAreaStatus } from 'src/components/ReceivingAreaStatus';
import { ItemVelocity } from 'src/components/ItemVelocity';
import { PropertySummary } from 'src/components/PropertySummary';
import { LowStockModal } from 'src/components/LowStockModal';
import { ExcessStockModal } from 'src/components/ExcessStockModal';
import { LowStockTable } from 'src/components/LowStockTable';
import { ExcessStockTable } from 'src/components/ExcessStockTable';
import { InventoryStorageDistribution } from 'src/components/InventoryStorageDistribution';
import { useDashboardStats, dashboardKeys, useInventoryByProperty } from 'src/hooks/useDashboardData';

const COLORS = [
  '#4F8DFD',
  '#22c55e',
  '#f59e42',
  '#ef4444',
  '#38bdf8',
  '#818cf8',
  '#f472b6',
  '#22d3ee'
];

export const Dashboard: React.FC = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { data: stats, isLoading: statsLoading } = useDashboardStats();

  // Inventory breakdown state
  const [selectedProperty, setSelectedProperty] = useState('brand');
  const { data: inventoryByProperty, isLoading: inventoryLoading, error: inventoryError } = useInventoryByProperty(selectedProperty);

  // Modal states
  const [lowStockModalOpen, setLowStockModalOpen] = useState(false);
  const [excessStockModalOpen, setExcessStockModalOpen] = useState(false);

  const handleRefreshAll = () => {
    queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        minHeight: '100vh',
        py: 4
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4
          }}
        >
          <Box>
            <Typography
              variant="h2"
              fontWeight={700}
              sx={{
                background: theme.colors.gradients.blue4,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              WMS Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Real-time warehouse management insights
            </Typography>
          </Box>
          <Tooltip title="Refresh all data">
            <IconButton
              onClick={handleRefreshAll}
              sx={{
                backgroundColor: theme.palette.background.paper,
                boxShadow: theme.colors.shadows.cardSm,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <StatCard
              title="Total Inventory"
              value={stats?.totalOnHandQuantity || 0}
              icon={<Inventory2 sx={{ fontSize: '2rem' }} />}
              gradient={theme.colors.gradients.blue4}
              loading={statsLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <StatCard
              title="Warehouses"
              value={stats?.totalWarehouses || 0}
              icon={<Warehouse sx={{ fontSize: '2rem' }} />}
              gradient={theme.colors.gradients.green1}
              loading={statsLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <StatCard
              title="Storage Units"
              value={stats?.totalStorageUnits || 0}
              icon={<Category sx={{ fontSize: '2rem' }} />}
              gradient={theme.colors.gradients.purple1}
              loading={statsLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <StatCard
              title="Low Stock Items"
              value={stats?.lowStockItemsCount || 0}
              icon={<Warning sx={{ fontSize: '2rem' }} />}
              gradient={
                (stats?.lowStockItemsCount || 0) > 0
                  ? theme.colors.gradients.orange1
                  : theme.colors.gradients.green2
              }
              loading={statsLoading}
              onClick={() => setLowStockModalOpen(true)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <StatCard
              title="High Stock Items"
              value={stats?.highStockItemsCount || 0}
              icon={<TrendingUp sx={{ fontSize: '2rem' }} />}
              gradient={
                (stats?.highStockItemsCount || 0) > 0
                  ? theme.colors.gradients.blue1
                  : theme.colors.gradients.green2
              }
              loading={statsLoading}
              onClick={() => setExcessStockModalOpen(true)}
            />
          </Grid>
        </Grid>

        {/* Operations Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} lg={6}>
            <ActiveTransferOrders />
          </Grid>
          <Grid item xs={12} lg={6}>
            <ShipmentsOverview />
          </Grid>
        </Grid>

        {/* Analytics Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Inventory Breakdown Chart and Storage Distribution side by side */}
          <Grid item xs={12} lg={6}>
            <InventoryBreakdownChart
              properties={['brand', 'season', 'category', 'gender']}
              selectedProperty={selectedProperty}
              onPropertyChange={setSelectedProperty}
              data={inventoryByProperty}
              isLoading={inventoryLoading}
              error={inventoryError}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <InventoryStorageDistribution
              data={inventoryByProperty}
              isLoading={inventoryLoading}
              error={inventoryError}
              colors={COLORS}
            />
          </Grid>

          {/* Warehouse Distribution and Top Storage Units */}
          <Grid item xs={12} lg={6}>
            <WarehouseUtilizationChart />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TopStorageUnits />
          </Grid>
        </Grid>

        {/* Recent Activity and Receiving Area side by side */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} lg={6}>
            <RecentActivityTimeline />
          </Grid>
          <Grid item xs={12} lg={6}>
            <ReceivingAreaStatus />
          </Grid>
        </Grid>

        {/* Stock Management Tables */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} lg={6}>
            <LowStockTable />
          </Grid>
          <Grid item xs={12} lg={6}>
            <ExcessStockTable />
          </Grid>
        </Grid>

        {/* Item Velocity - Full Width */}
        <Box sx={{ mb: 4 }}>
          <ItemVelocity />
        </Box>

        {/* Property Summary - Full Width */}
        {/* <Box sx={{ mb: 4 }}>
          <PropertySummary />
        </Box> */}

        {/* Stale Inventory Table - Full Width */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StaleInventoryTable />
          </Grid>
        </Grid>

        {/* Modals */}
        <LowStockModal
          open={lowStockModalOpen}
          onClose={() => setLowStockModalOpen(false)}
        />
        <ExcessStockModal
          open={excessStockModalOpen}
          onClose={() => setExcessStockModalOpen(false)}
        />
      </Container>
    </Box>
  );
};

export default Dashboard;